import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';

export interface AnalyticsEventProperties {
  [key: string]: string | number | boolean | null | Array<string | number | boolean | null> | undefined;
}

export interface AnalyticsEventPayload {
  event: string;
  properties?: AnalyticsEventProperties;
  timestamp?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor(private apiService: ApiService) {}

  track(event: string, properties?: AnalyticsEventProperties, timestamp?: string): void {
    if (!event || event.length > 255) {
      console.warn('[AnalyticsService] Invalid event name skipped', event);
      return;
    }

    const prefixedEvent = `T4B_${event}`;

    const payload: AnalyticsEventPayload = { event: prefixedEvent };

    if (properties) {
      payload.properties = this.sanitiseProperties(properties);
    }

    if (timestamp) {
      payload.timestamp = timestamp;
    }

    this.enqueueDispatch(payload);
  }

  private sanitiseProperties(properties: AnalyticsEventProperties): AnalyticsEventProperties {
    const result: AnalyticsEventProperties = {};

    Object.entries(properties).forEach(([key, value]) => {
      if (value === undefined) {
        return;
      }

      if (Array.isArray(value)) {
        const filtered = value.filter(
          (item) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean' || item === null,
        );
        result[key] = filtered as Array<string | number | boolean | null>;
        return;
      }

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
        result[key] = value;
      } else {
        result[key] = String(value);
      }
    });

    return result;
  }

  private enqueueDispatch(payload: AnalyticsEventPayload): void {
    const dispatch = () => {
      this.apiService.post('analytics/events', payload, true).subscribe({
        next: () => {
          /* fire-and-forget */
        },
        error: (error) => {
          console.warn('[AnalyticsService] Failed to send analytics event', payload.event, error);
        },
      });
    };

    if (typeof queueMicrotask === 'function') {
      queueMicrotask(dispatch);
    } else {
      Promise.resolve().then(dispatch);
    }
  }
}
